import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { getFreshToken } from '@/lib/auth-token';

interface SettingRow { key: string; value: string; label: string; }

const SAVE_SETTINGS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/save-settings`;

const DEFAULT_CATEGORIAS = [
  'Competências Transversais',
  'Tecnologias de Informação',
  'Gestão e Liderança',
  'Legislação e Regulamentação',
  'Financeiro e Contabilidade',
  'Saúde e Segurança',
  'Línguas',
  'Técnica Especializada',
  'Outra',
];

export default function SettingsFormacaoTab() {
  const { activeMunicipio } = useActiveMunicipio();
  const municipioId = activeMunicipio?.id ?? '00000000-0000-0000-0000-000000000001';

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Emails restritos à Formação
  const [emailsFormacao, setEmailsFormacao] = useState<string>('');
  const [savingEmails, setSavingEmails] = useState(false);
  const [novoEmailFormacao, setNovoEmailFormacao] = useState<string>('');
  const [emailsFormacaoMsg, setEmailsFormacaoMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Categorias personalizáveis
  const [categorias, setCategorias] = useState<string[]>(DEFAULT_CATEGORIAS);
  const [novaCategoria, setNovaCategoria] = useState<string>('');
  const [savingCategorias, setSavingCategorias] = useState(false);
  const [categoriasMsg, setCategoriasMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Notificações automáticas por email
  const [notifSemFormacao, setNotifSemFormacao] = useState({ enabled: false, limiar: 12, destino: '' });
  const [notifProxima, setNotifProxima] = useState({ enabled: false, dias: 7, destino: '' });
  const [notifAtrasada, setNotifAtrasada] = useState({ enabled: false, destino: '' });
  const [savingNotif, setSavingNotif] = useState(false);
  const [notifMsg, setNotifMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const emailsFormacaoList = emailsFormacao
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);

  useEffect(() => {
    supabase
      .from('app_settings')
      .select('*')
      .eq('municipio_id', municipioId)
      .order('key')
      .then(({ data }) => {
        if (data) {
          const e: Record<string, string> = {};
          data.forEach((s: SettingRow) => { e[s.key] = s.value; });
          setEdits(e);
          const emailsRow = data.find((s: SettingRow) => s.key === 'formacao_only_emails');
          if (emailsRow) setEmailsFormacao(emailsRow.value);
          const catRow = data.find((s: SettingRow) => s.key === 'formacao_categorias');
          if (catRow && catRow.value.trim()) {
            setCategorias(catRow.value.split(',').map((c: string) => c.trim()).filter(Boolean));
          }
          const nf1 = data.find((s: SettingRow) => s.key === 'formacao_notif_sem_formacao');
          if (nf1 && nf1.value.trim()) {
            try { setNotifSemFormacao(JSON.parse(nf1.value)); } catch { /* ignora */ }
          }
          const nf2 = data.find((s: SettingRow) => s.key === 'formacao_notif_proxima');
          if (nf2 && nf2.value.trim()) {
            try { setNotifProxima(JSON.parse(nf2.value)); } catch { /* ignora */ }
          }
          const nf3 = data.find((s: SettingRow) => s.key === 'formacao_notif_atrasada');
          if (nf3 && nf3.value.trim()) {
            try { setNotifAtrasada(JSON.parse(nf3.value)); } catch { /* ignora */ }
          }
        }
      });
  }, [municipioId]);

  const set = (key: string, val: string) => setEdits((prev) => ({ ...prev, [key]: val }));

  const addEmailFormacao = () => {
    const email = novoEmailFormacao.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (emailsFormacaoList.includes(email)) return;
    const updated = [...emailsFormacaoList, email].join(',');
    setEmailsFormacao(updated);
    setNovoEmailFormacao('');
  };

  const removeEmailFormacao = (email: string) => {
    const updated = emailsFormacaoList.filter((e) => e !== email).join(',');
    setEmailsFormacao(updated);
  };

  const saveEmailsFormacao = async () => {
    setSavingEmails(true);
    setEmailsFormacaoMsg(null);
    try {
      const token = await getFreshToken();
      const res = await fetch(SAVE_SETTINGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: { formacao_only_emails: emailsFormacao } }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);
      setEmailsFormacaoMsg({ type: 'success', text: 'Emails de acesso à Formação guardados!' });
    } catch (err: unknown) {
      setEmailsFormacaoMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao guardar.' });
    } finally {
      setSavingEmails(false);
      setTimeout(() => setEmailsFormacaoMsg(null), 5000);
    }
  };

  // ── Categorias ─────────────────────────────────────────────────

  const addCategoria = () => {
    const cat = novaCategoria.trim();
    if (!cat) return;
    if (categorias.includes(cat)) {
      setCategoriasMsg({ type: 'error', text: 'Esta categoria já existe.' });
      setTimeout(() => setCategoriasMsg(null), 3000);
      return;
    }
    setCategorias((prev) => [...prev, cat]);
    setNovaCategoria('');
  };

  const removeCategoria = (cat: string) => {
    setCategorias((prev) => prev.filter((c) => c !== cat));
  };

  const reorderCategoria = (index: number, direction: 'up' | 'down') => {
    setCategorias((prev) => {
      const arr = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const saveCategorias = async () => {
    setSavingCategorias(true);
    setCategoriasMsg(null);
    try {
      const token = await getFreshToken();
      const res = await fetch(SAVE_SETTINGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: { formacao_categorias: categorias.join(',') } }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);
      setCategoriasMsg({ type: 'success', text: 'Categorias de formação guardadas!' });
    } catch (err: unknown) {
      setCategoriasMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao guardar.' });
    } finally {
      setSavingCategorias(false);
      setTimeout(() => setCategoriasMsg(null), 5000);
    }
  };

  const saveNotificacoes = async () => {
    setSavingNotif(true);
    setNotifMsg(null);
    try {
      const token = await getFreshToken();
      const res = await fetch(SAVE_SETTINGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          settings: {
            formacao_notif_sem_formacao: JSON.stringify(notifSemFormacao),
            formacao_notif_proxima: JSON.stringify(notifProxima),
            formacao_notif_atrasada: JSON.stringify(notifAtrasada),
          },
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);
      setNotifMsg({ type: 'success', text: 'Notificações automáticas guardadas!' });
    } catch (err: unknown) {
      setNotifMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao guardar.' });
    } finally {
      setSavingNotif(false);
      setTimeout(() => setNotifMsg(null), 6000);
    }
  };

  const handleSaveFormacao = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const token = await getFreshToken();
      const formacaoKeys = [
        'formacao_horas_minimas_anuais',
        'formacao_email_notificacoes',
        'formacao_email_remetente_nome',
        'formacao_relatorio_rodape',
        'formacao_relatorio_titulo',
      ];
      const settingsToSave: Record<string, string> = {};
      formacaoKeys.forEach((k) => {
        if (edits[k] !== undefined) settingsToSave[k] = edits[k];
      });

      const res = await fetch(SAVE_SETTINGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: settingsToSave }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);
      setMsg({ type: 'success', text: 'Configurações de Formação guardadas com sucesso!' });
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro desconhecido.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Categorias de Formação ── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
            <i className="ri-folder-chart-line text-amber-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Categorias / Áreas de Formação</h2>
            <p className="text-xs text-slate-400 mt-0.5">Áreas temáticas disponíveis para classificar ações de formação</p>
          </div>
        </div>
        <div className="p-6 space-y-4">

          {/* Lista de categorias */}
          {categorias.length === 0 ? (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/40">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line text-slate-400 text-sm" />
              </div>
              <p className="text-xs text-slate-400">Nenhuma categoria configurada. As categorias padrão serão usadas.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {categorias.map((cat, index) => (
                <div key={cat} className="flex items-center gap-2 px-3 py-2.5 bg-slate-50/60 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="w-5 h-5 flex items-center justify-center rounded-md bg-slate-200 text-slate-500 flex-shrink-0">
                    <span className="text-[10px] font-bold">{index + 1}</span>
                  </div>
                  <span className="text-sm text-slate-700 font-medium flex-1">{cat}</span>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => reorderCategoria(index, 'up')}
                      disabled={index === 0}
                      className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Mover para cima"
                    >
                      <i className="ri-arrow-up-line text-xs" />
                    </button>
                    <button
                      onClick={() => reorderCategoria(index, 'down')}
                      disabled={index === categorias.length - 1}
                      className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Mover para baixo"
                    >
                      <i className="ri-arrow-down-line text-xs" />
                    </button>
                    <button
                      onClick={() => removeCategoria(cat)}
                      className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                      title="Remover"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Adicionar nova categoria */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Nova categoria (ex: Comunicação, Urbanismo...)"
              value={novaCategoria}
              onChange={(e) => setNovaCategoria(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCategoria()}
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300 transition-colors"
            />
            <button
              onClick={addCategoria}
              disabled={!novaCategoria.trim()}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm" />
              </div>
              Adicionar
            </button>
          </div>

          {/* Feedback categorias */}
          {categoriasMsg && (
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${
              categoriasMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-sm ${
                  categoriasMsg.type === 'success' ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-red-500'
                }`} />
              </div>
              <p className={`text-sm ${categoriasMsg.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
                {categoriasMsg.text}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-[11px] text-slate-400">{categorias.length} categoria(s) configurada(s)</p>
            <button
              onClick={saveCategorias}
              disabled={savingCategorias}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-60 transition-colors cursor-pointer"
            >
              {savingCategorias
                ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
                : <><i className="ri-save-line" />Guardar Categorias</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* ── Parâmetros Gerais da Formação ── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg">
            <i className="ri-settings-3-line text-emerald-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Parâmetros Gerais</h2>
            <p className="text-xs text-slate-400 mt-0.5">Configurações base do módulo de Gestão da Formação</p>
          </div>
        </div>
        <div className="p-6 space-y-5">

          {/* Horas mínimas anuais */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-time-line text-slate-400" />
              Requisitos de Horas
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Horas mínimas anuais por funcionário</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0} max={500} step={1}
                    placeholder="ex: 35"
                    value={edits['formacao_horas_minimas_anuais'] ?? '35'}
                    onChange={(e) => set('formacao_horas_minimas_anuais', e.target.value)}
                    className="w-24 px-3 py-2 text-sm font-bold text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 transition-colors"
                  />
                  <span className="text-sm text-slate-400">horas/ano</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">Usado para alertas de requisitos de formação</p>
              </div>
            </div>
          </div>

          {/* Notificações por email */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-mail-settings-line text-slate-400" />
              Notificações por Email
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Email para notificações de formação</label>
                <input
                  type="email"
                  placeholder="ex: formacao@camara.pt"
                  value={edits['formacao_email_notificacoes'] ?? ''}
                  onChange={(e) => set('formacao_email_notificacoes', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Nome do remetente</label>
                <input
                  type="text"
                  placeholder="ex: Gestão da Formação"
                  value={edits['formacao_email_remetente_nome'] ?? 'Gestão da Formação'}
                  onChange={(e) => set('formacao_email_remetente_nome', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Relatórios */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-file-chart-line text-slate-400" />
              Relatórios e Exportação
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Título dos relatórios PDF</label>
                <input
                  type="text"
                  placeholder="ex: Relatório de Formação"
                  value={edits['formacao_relatorio_titulo'] ?? 'Relatório de Formação'}
                  onChange={(e) => set('formacao_relatorio_titulo', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Rodapé dos relatórios PDF</label>
                <input
                  type="text"
                  placeholder="ex: Câmara Municipal — Confidencial"
                  value={edits['formacao_relatorio_rodape'] ?? ''}
                  onChange={(e) => set('formacao_relatorio_rodape', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 transition-colors"
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Acesso Restrito à Formação ── */}
      <div className="bg-white rounded-2xl border border-emerald-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-emerald-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg">
            <i className="ri-lock-password-line text-emerald-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Acesso Restrito — Gestão da Formação</h2>
            <p className="text-xs text-slate-400 mt-0.5">Utilizadores que só vêem o módulo de Formação (independentemente do módulo ativo)</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Os emails abaixo têm acesso exclusivo ao módulo de <strong>Gestão da Formação</strong> e são automaticamente redireccionados para esse módulo ao iniciar sessão, independentemente do módulo que estiver ativo.
          </p>

          {/* Lista de emails */}
          <div className="space-y-2">
            {emailsFormacaoList.length === 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-emerald-200 bg-emerald-50/30">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line text-emerald-400 text-sm" />
                </div>
                <p className="text-xs text-emerald-600">Nenhum email configurado. Apenas o módulo ativo determina o redirecionamento.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {emailsFormacaoList.map((email) => (
                  <div key={email} className="flex items-center justify-between gap-3 px-3 py-2.5 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                        <i className="ri-user-line text-xs text-emerald-600" />
                      </div>
                      <span className="text-sm text-slate-700 font-medium truncate">{email}</span>
                    </div>
                    <button
                      onClick={() => removeEmailFormacao(email)}
                      className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                      title="Remover"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Adicionar novo email */}
          <div className="flex items-center gap-2">
            <input
              type="email"
              placeholder="novo.utilizador@exemplo.pt"
              value={novoEmailFormacao}
              onChange={(e) => setNovoEmailFormacao(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addEmailFormacao()}
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-300 transition-colors"
            />
            <button
              onClick={addEmailFormacao}
              disabled={!novoEmailFormacao.trim() || !novoEmailFormacao.includes('@')}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm" />
              </div>
              Adicionar
            </button>
          </div>

          {/* Feedback emails */}
          {emailsFormacaoMsg && (
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${
              emailsFormacaoMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-sm ${
                  emailsFormacaoMsg.type === 'success' ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-red-500'
                }`} />
              </div>
              <p className={`text-sm ${emailsFormacaoMsg.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
                {emailsFormacaoMsg.text}
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={saveEmailsFormacao}
              disabled={savingEmails}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors cursor-pointer"
            >
              {savingEmails
                ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
                : <><i className="ri-save-line" />Guardar Emails</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* ── Notificações Automáticas por Email ── */}
      <div className="bg-white rounded-2xl border border-blue-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-blue-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-blue-50 rounded-lg">
            <i className="ri-mail-settings-line text-blue-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Notificações Automáticas por Email</h2>
            <p className="text-xs text-slate-400 mt-0.5">Alertas e lembretes automáticos do módulo de Formação</p>
          </div>
        </div>
        <div className="p-6 space-y-5">

          {/* 1. Sem formação há X meses */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
                  <i className="ri-user-follow-line text-amber-600 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Funcionários sem formação</p>
                  <p className="text-xs text-slate-400">Notificar funcionários que não têm formação registada há X meses</p>
                </div>
              </div>
              <div
                onClick={() => setNotifSemFormacao((prev) => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${notifSemFormacao.enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifSemFormacao.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
            {notifSemFormacao.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Limiar de meses sem formação</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={60} step={1}
                      value={notifSemFormacao.limiar}
                      onChange={(e) => setNotifSemFormacao((prev) => ({ ...prev, limiar: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                      className="w-20 px-3 py-2 text-sm font-bold text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition-colors"
                    />
                    <span className="text-sm text-slate-400">meses</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Email de destino (opcional — CC)</label>
                  <input
                    type="email"
                    placeholder="ex: formacao@camara.pt"
                    value={notifSemFormacao.destino}
                    onChange={(e) => setNotifSemFormacao((prev) => ({ ...prev, destino: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition-colors"
                  />
                  <p className="text-xs text-slate-400 mt-1">Se preenchido, copia também este email em cada notificação</p>
                </div>
              </div>
            )}
          </div>

          {/* 2. Formação próxima a começar */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg">
                  <i className="ri-calendar-check-line text-emerald-600 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Lembrete de formação próxima</p>
                  <p className="text-xs text-slate-400">Notificar participantes X dias antes do início</p>
                </div>
              </div>
              <div
                onClick={() => setNotifProxima((prev) => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${notifProxima.enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifProxima.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
            {notifProxima.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Dias de antecedência</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={60} step={1}
                      value={notifProxima.dias}
                      onChange={(e) => setNotifProxima((prev) => ({ ...prev, dias: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                      className="w-20 px-3 py-2 text-sm font-bold text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition-colors"
                    />
                    <span className="text-sm text-slate-400">dias</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Email de destino (opcional — CC)</label>
                  <input
                    type="email"
                    placeholder="ex: formacao@camara.pt"
                    value={notifProxima.destino}
                    onChange={(e) => setNotifProxima((prev) => ({ ...prev, destino: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition-colors"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 3. Ações atrasadas */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
                  <i className="ri-alarm-warning-line text-red-600 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Ações de formação atrasadas</p>
                  <p className="text-xs text-slate-400">Alerta quando ações "Planeadas" já passaram a data</p>
                </div>
              </div>
              <div
                onClick={() => setNotifAtrasada((prev) => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${notifAtrasada.enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifAtrasada.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
            {notifAtrasada.enabled && (
              <div className="pt-2 border-t border-slate-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Email do gestor de formação</label>
                    <input
                      type="email"
                      placeholder="ex: gestor.formacao@camara.pt"
                      value={notifAtrasada.destino}
                      onChange={(e) => setNotifAtrasada((prev) => ({ ...prev, destino: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition-colors"
                    />
                    <p className="text-xs text-slate-400 mt-1">Email que recebe o resumo das ações atrasadas</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Feedback notificações */}
          {notifMsg && (
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${notifMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-sm ${notifMsg.type === 'success' ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-red-500'}`} />
              </div>
              <p className={`text-sm ${notifMsg.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{notifMsg.text}</p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={saveNotificacoes}
              disabled={savingNotif}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors cursor-pointer"
            >
              {savingNotif
                ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
                : <><i className="ri-save-line" />Guardar Notificações</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Feedback geral */}
      {msg && (
        <div className={`flex items-center gap-2 p-3 rounded-lg border ${msg.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`text-sm ${msg.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'}`} />
          </div>
          <p className={`text-sm ${msg.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>
        </div>
      )}

      <div className="flex justify-end pb-2">
        <button
          onClick={handleSaveFormacao}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          {saving
            ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
            : <><i className="ri-save-line" />Guardar Configurações</>
          }
        </button>
      </div>
    </div>
  );
}
